import { useEffect, useRef, useState } from 'react';
import { useT, useUiStore } from '@/store';
import { useCriticalToast, useNotifications, useResolveNotification } from '@/hooks/useNotifications';
import { formatOrderDateTime } from '@/lib/format';

const SEVERITY_TONE = {
  critical: 'text-red',
  warning: 'text-amber',
  resolved: 'text-green',
};

/**
 * The header bell: compliance deadlines, grounded vehicles, and dwell
 * breaches that a dispatcher may have missed while on another tab.
 *
 * The badge counts what has arrived since the admin last opened the panel,
 * persisted across reloads — a count that resets on every refresh teaches
 * people to ignore it.
 */
export function NotificationBell() {
  const t = useT();
  const lang = useUiStore((s) => s.lang);
  const { notifications, unreadCount, markSeen } = useNotifications();
  const resolveNotification = useResolveNotification();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Click-outside close: a dropdown that only closes via its own button is
  // a dropdown people leave open over the thing they were reading.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const toggle = () => {
    setOpen((wasOpen) => {
      if (!wasOpen) markSeen();
      return !wasOpen;
    });
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        className="btn relative !px-2.5"
        onClick={toggle}
        aria-label={t('notif.title')}
        aria-expanded={open}
      >
        🔔
        {unreadCount > 0 ? (
          <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red px-1 text-[9px] font-bold text-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="panel absolute right-0 z-[100] mt-2 max-h-[420px] w-[320px] overflow-y-auto !p-2 shadow-2xl">
          <h3 className="panel-title px-2 pt-1">{t('notif.title')}</h3>
          {notifications.length === 0 ? (
            <p className="px-2 py-4 text-center text-[12px] text-dim">{t('notif.empty')}</p>
          ) : (
            <ul className="space-y-1">
              {notifications.map((notification) => (
                <li key={notification.id} className="rounded-[9px] px-2 py-2 hover:bg-white/4">
                  <p className={`text-[12px] leading-snug ${SEVERITY_TONE[notification.severity] ?? 'text-ink'}`}>
                    {notification.message}
                  </p>
                  <div className="mt-0.5 flex items-center justify-between gap-2">
                    <span className="text-[10px] text-dim">
                      {formatOrderDateTime(notification.created_at, lang)}
                    </span>
                    {notification.resolvable ? (
                      <button
                        type="button"
                        className="shrink-0 text-[10px] font-semibold text-accent hover:underline disabled:opacity-50"
                        disabled={resolveNotification.isPending}
                        onClick={(event) => {
                          event.stopPropagation();
                          resolveNotification.mutate(notification.id);
                        }}
                      >
                        {t('notif.markResolved')}
                      </button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The interrupting half: a compliance deadline inside 7 days, a grounded
 * truck, or a dwell breach is worth a toast on whatever tab is open, because
 * each one costs money per hour it goes unseen. Everything else stays in the
 * bell. Clicking the toast goes to the view that can act on it.
 */
export function CriticalToast() {
  const t = useT();
  const setView = useUiStore((s) => s.setView);
  const { toast, dismiss } = useCriticalToast();
  if (!toast) return null;

  return (
    <div className="fixed bottom-5 right-5 z-[300] max-w-[340px]">
      <button
        type="button"
        onClick={() => {
          setView(toast.targetView);
          dismiss();
        }}
        className="panel block w-full border-red/40 bg-red/10 text-left !p-3 shadow-2xl"
      >
        <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.03em] text-red">
          {t('transportas.toastCritical')}
        </div>
        <p className="text-[12.5px] leading-snug">{toast.message}</p>
      </button>
    </div>
  );
}
